import { useState, useCallback, useRef } from "react";
import { Plus } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SightingsHeatmap from "@/components/map/SightingsHeatmap";
import SightingsSidebar from "@/components/map/SightingsSidebar";
import BulkSightingImport from "@/components/map/BulkSightingImport";
import { useSightingLocations } from "@/hooks/useSightingLocations";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/hooks/useSEO";

const HeatmapPage = () => {
  useSEO({
    title: "Loch Ness Sightings Heatmap | Loch Ness Story",
    description: "Interactive heatmap showing all documented Loch Ness Monster sighting locations. Explore where Nessie has been spotted throughout history.",
    canonical: "/heatmap",
    ogImage: "/lovable-uploads/74638626-9ce3-4018-94b3-ec8980da02b9.png",
  });
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const { data: locations = [], isLoading } = useSightingLocations();
  const mapRef = useRef<HTMLDivElement>(null);

  const handleHover = useCallback((id: string | null) => {
    setHighlightedId(id);
  }, []);

  const handleClick = useCallback((id: string) => {
    setHighlightedId(id);
    // On mobile, scroll the map into view
    if (window.innerWidth < 1024 && mapRef.current) {
      mapRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-2">
              Sightings Heatmap
            </h1>
            <p className="text-muted-foreground text-sm md:text-base max-w-2xl">
              Explore reported Loch Ness Monster sightings. Hover over markers or the list to highlight locations.
            </p>
          </div>
          <Button size="sm" onClick={() => setShowImport(true)} className="shrink-0">
            <Plus className="h-4 w-4 mr-1" /> Add Sightings
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64 text-muted-foreground">Loading sightings…</div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4 lg:h-[calc(100vh-220px)]">
            <div className="lg:w-[340px] flex-shrink-0 lg:h-full overflow-hidden flex flex-col">
              <SightingsSidebar
                locations={locations}
                highlightedId={highlightedId}
                onItemHover={handleHover}
                onItemClick={handleClick}
              />
            </div>
            <div ref={mapRef} className="flex-1 min-h-[400px]">
              <SightingsHeatmap
                locations={locations}
                highlightedId={highlightedId}
                onMarkerHover={handleHover}
                onMarkerClick={handleClick}
              />
            </div>
          </div>
        )}
      </main>
      <Footer />
      <BulkSightingImport open={showImport} onClose={() => setShowImport(false)} />
    </div>
  );
};

export default HeatmapPage;
